import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { ErrorBoundary as RollbarErrorBoundary } from '@rollbar/react';
import { jwtDecode } from 'jwt-decode';
import { use, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import LocaleContext from '../components/hooks/LocaleContext';
import NavbarTop from '../components/navbar/NavbarTop';
import { TermsModal } from '../components/terms/TermsModal';
import { privacyPromptSections } from '../components/terms/privacyPrompt';
import { setAnalyticsUser, suspendAnalytics, useConsentStatus } from '../analytics/consent';
import { trackError } from '../analytics/track';
import { isImpersonatedSession } from '../components/accesstoken';
import { ZendeskMessengerApi } from '../components/ZendeskWidget/zendesk';
import { config } from '../config';

const USER_QUERY = gql`
  query {
    viewer {
      id
      firstName
      lastName
      email
      phone
      settings {
        termsAcceptedAt
        emailProductUpdates
      }
      canActAs {
        id
        email
      }
      client {
        viewerIsAdmin
        id
        orgName
        settings {
          anonymized
          showPassed
        }
      }
    }
  }
`;

const ZENDESK_AUTH = gql`
  query ZendeskMessagingAuthentication {
    ZendeskMessagingAuthentication {
      token
    }
  }
`;

const ErrorMessage = ({ error }: { error: Error | null }) => {
  const { t } = useTranslation();
  console.info('An error occurred.', { error });
  useEffect(() => {
    if (error) trackError('crash');
  }, [error]);
  return <>{error && <h2>{t('dashboardLayout.error.message')}</h2>}</>;
};

const DashboardLayout = () => {
  const { t } = useTranslation();
  const { userSettings, setUserVariables } = use(LocaleContext);
  const { data, loading: viewerLoading, error: viewerError } = useQuery(USER_QUERY);
  const [authZendesk] = useLazyQuery(ZENDESK_AUTH);
  const location = useLocation();

  useEffect(() => {
    let zendeskMessagingTokenTimer: number;

    const renewZendeskToken = async () => {
      const zendeskResult = await authZendesk();
      const token = zendeskResult?.data?.ZendeskMessagingAuthentication?.token;
      if (!token) return undefined;
      const decoded_token = jwtDecode(token);

      await ZendeskMessengerApi.login(token);
      const now = new Date();
      const tokenExpires = new Date(decoded_token.exp ?? 0);
      // Time to renew 5 minutes before token expires
      const timeToRenew = tokenExpires.getTime() - now.getTime() - 300000;

      return timeToRenew;
    };

    if (config.PROD) {
      renewZendeskToken().then((timeToRenew) => {
        if (timeToRenew) zendeskMessagingTokenTimer = setInterval(renewZendeskToken, timeToRenew);
      });
    }

    return () => {
      clearInterval(zendeskMessagingTokenTimer);
    };
  }, [authZendesk]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!data) return;
    const viewer = data.viewer;
    const client = viewer.client;
    setUserVariables?.({
      user: {
        id: viewer.id,
        firstName: viewer.firstName,
        lastName: viewer.lastName,
        email: viewer.email,
        phone: viewer.phone,
        contact: viewer.contact,
        company: {
          admin: client ? (client.viewerIsAdmin ? client.viewerIsAdmin : false) : false,
          name: client ? client.orgName : null,
          id: client ? client.id : null,
        },
        settings: {
          anonymized: client ? (client.settings.anonymized ? client.settings.anonymized : false) : false,
          showPassed: client ? (client.settings.showPassed !== null ? client.settings.showPassed : true) : true,
          termsAcceptedAt: viewer && viewer.settings.termsAcceptedAt,
          emailProductUpdates: viewer && viewer.settings.emailProductUpdates,
        },
        canActAs: viewer.canActAs,
      },
    });

    // Feature flags always get the company and user ID (see consent.ts); analytics only after
    // consent, and analytics only ever gets a random user ID and non-personal flags.
    if (viewer.id) {
      if (isImpersonatedSession()) suspendAnalytics();
      setAnalyticsUser(
        {
          id: viewer.id,
          clientId: client?.id,
          email: viewer.email,
          isAdmin: !!client?.viewerIsAdmin,
          isInternal: (viewer.email ?? '').toLowerCase().endsWith('@hubert.ai'),
        },
        client?.orgName,
      );
    }
  }, [data, setUserVariables]);

  const consentStatus = useConsentStatus();
  const prompt = privacyPromptSections({
    termsAcceptedAt: userSettings.settings?.termsAcceptedAt,
    consentStatus,
    impersonating: isImpersonatedSession(),
  });
  // The app stays hidden behind the privacy window until it is answered, as with the old terms window.
  const termsAccepted = !prompt.policy;
  const showPrivacyWindow = !viewerLoading && !viewerError && (prompt.policy || prompt.analytics);

  return (
    <>
      <NavbarTop />
      <div className="main-container p-0 mx-auto">
        {viewerLoading && t('dashboardLayout.loading')}
        {!viewerLoading && viewerError && <ErrorMessage error={viewerError} />}
        {showPrivacyWindow && (
          <TermsModal
            setUserVariables={setUserVariables!}
            showPolicy={prompt.policy}
            showAnalytics={prompt.analytics}
            policyUpdated={!!userSettings.settings?.termsAcceptedAt}
          />
        )}
        {!viewerLoading && !viewerError && termsAccepted && (
          <RollbarErrorBoundary fallbackUI={ErrorMessage}>
            <Outlet />
          </RollbarErrorBoundary>
        )}
      </div>
    </>
  );
};

export default DashboardLayout;
